import { useState } from 'react';
import { useTranslate, useShow, useNavigation, useOne } from '@refinedev/core';
import { Show, TextField, DateField } from '@refinedev/antd';
import { Typography, Divider, Button } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type {
  ContentImprintDetailOut,
  ContentPublisherOut,
} from '@/api/generated/schemas';
import { Provider } from '@/api/generated/schemas';
import { ImprintRenameModal } from '@/components/content_catalog/imprints/ImprintRenameModal';

const { Title } = Typography;

export const ContentCatalogImprintShow = () => {
  const t = useTranslate();
  const { showUrl } = useNavigation();
  const [renameOpen, setRenameOpen] = useState(false);
  const { query } = useShow<ContentImprintDetailOut>({
    resource: 'content_catalog.imprints',
  });
  const { data, isLoading } = query;
  const record = data?.data;

  // Elibri imprints come only from the feed; only Novelist ones are editable.
  const { result: publisher } = useOne<ContentPublisherOut>({
    resource: 'content_catalog.publishers',
    id: record?.publisher_id,
    queryOptions: { enabled: !!record },
  });
  const canRename = publisher?.provider === Provider.novelist;

  return (
    <Show
      isLoading={isLoading}
      canEdit={false}
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          {canRename && (
            <Button icon={<EditOutlined />} onClick={() => setRenameOpen(true)}>
              {t('content_catalog.imprints.rename.button')}
            </Button>
          )}
        </>
      )}
    >
      {record && (
        <ImprintRenameModal
          open={renameOpen}
          onClose={() => setRenameOpen(false)}
          imprint={record}
        />
      )}
      {record && (
        <>
          <Title level={5}>{t('base.fields.id')}</Title>
          <TextField value={record.id} />

          <Title level={5}>{t('content_catalog.imprints.fields.name')}</Title>
          <TextField value={record.name} />

          <Title level={5}>
            {t('content_catalog.imprints.fields.publisher')}
          </Title>
          <Typography.Link
            href={showUrl('content_catalog.publishers', record.publisher_id)}
          >
            {record.publisher_name ?? `#${record.publisher_id}`}
          </Typography.Link>

          <Divider />

          <Title level={5}>{t('base.fields.created_at')}</Title>
          <DateField value={record.created_at} format="LLL" />

          <Title level={5}>{t('base.fields.updated_at')}</Title>
          <DateField value={record.updated_at} format="LLL" />
        </>
      )}
    </Show>
  );
};
